// Feature 012 — the "Know" tier as a pure VIEW (REQ-ALERT-002). Ingested FYI mail (a charge notice,
// a service change) surfaces as a dismissible heads-up on Today. No item logic, no health content —
// titles are already clean upstream (only non-health mail reaches extraction; Principle 2).

import type { Extraction } from "./extract";

/** A stored heads-up (mirrors public.alert). `dismissed_at` null ⇒ still active. */
export interface AlertRow {
  id: string;
  title: string;
  summary?: string | null;
  url?: string | null;
  created_at: string;
  dismissed_at?: string | null;
  capture_id?: string | null; // the ingested capture this heads-up came from (for provenance on promote)
}

export interface AlertItem {
  id: string;
  title: string;
  summary: string | null;
  url: string | null;
  createdAt: string;
  captureId?: string | null; // links a promoted item back to the source email (REQ-ALERT-003); activeAlerts always sets it
}

/** Promote a heads-up into an editable DRAFT (REQ-ALERT-003): a `task` prefilled from the heads-up's
 *  title + summary, `ready:false` so the review drawer opens for the family to pick task-vs-event and a
 *  date before it commits. Pure — the caller opens the drawer and dismisses the alert on commit. */
export function alertToExtraction(alert: AlertItem): Extraction {
  return {
    draft: {
      type: "task",
      title: alert.title,
      owners: [],
      due_at: null,
      end_at: null,
      location: null,
      rrule: null,
      details: alert.summary,
      priority: null,
    },
    blockingField: null,
    clarifyingQuestion: null,
    ready: false,
  };
}

/** Keep only safe, external http(s) links — never javascript:/data: etc. (a link comes from mail/Drive). */
function safeUrl(u: string | null | undefined): string | null {
  if (typeof u !== "string") return null;
  return /^https?:\/\//i.test(u.trim()) ? u.trim() : null;
}

/** The active heads-ups: not dismissed, newest first. Pure. */
export function activeAlerts(rows: AlertRow[]): AlertItem[] {
  return (rows ?? [])
    .filter((r) => r && typeof r.id === "string" && !r.dismissed_at && typeof r.title === "string" && r.title.trim())
    .slice()
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    .map((r) => ({ id: r.id, title: r.title, summary: r.summary?.trim() || null, url: safeUrl(r.url), createdAt: r.created_at, captureId: r.capture_id ?? null }));
}
